// receiptPrintService.js - Frontend-only solution
import moment from 'moment';

class ReceiptPrintService {
  constructor() {
    this.restaurantInfo = {
      name: 'Xtream Nutrition',
      address: 'Barsha Mall',
      city: 'Dubai, UAE',
      phone: '+971 XX XXX XXXX', // Update with your phone
      taxId: 'TRN: 100000000000000', // Update with your TRN
      email: 'info@pro360erp.com',
      website: 'www.pro360erp.com',
    };

    this.printerConfig = this.loadPrinterConfig();
  }

  // Load printer configuration from localStorage
  loadPrinterConfig() {
    const savedConfig = localStorage.getItem('printerConfig');
    return savedConfig
      ? JSON.parse(savedConfig)
      : {
          paperWidth: 80, // 80mm or 58mm
          enableAutoprint: true,
          copies: 1,
          showPreview: false, // Show preview before printing
          printLogo: true,
          printBarcode: true,
          printQRCode: false,
        };
  }

  // Save printer configuration
  savePrinterConfig(config) {
    this.printerConfig = { ...this.printerConfig, ...config };
    localStorage.setItem('printerConfig', JSON.stringify(this.printerConfig));
  }

  // Main print method
  async printReceipt(orderData, currency) {
    try {
      const receiptHTML = this.generateReceiptHTML(orderData, currency);

      if (this.printerConfig.showPreview) {
        return await this.printWithPreview(receiptHTML);
      } else {
        return await this.printDirectly(receiptHTML);
      }
    } catch (error) {
      console.error('Print error:', error);
      throw error;
    }
  }

  // Generate receipt HTML
  generateReceiptHTML(orderData, currency) {
    const {
      orderId,
      orderDate,
      orderItems,
      subtotal,
      tax,
      discount,
      total,
      amountPaid,
      change,
      customerName,
      orderType,
      invoiceNumber,
      paymentMethod,
      tableNumber,
    } = orderData;

    const currencyCode = currency?.code || 'AED';

    // Generate QR code data (can be order details URL)
    const qrData = `https://pro360erp.com/order/${orderId}`;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Receipt #${orderId}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;600&display=swap');
          
          @media print {
            @page {
              margin: 0;
              size: ${this.printerConfig.paperWidth}mm auto;
            }
            body {
              margin: 0;
              padding: 0;
            }
            .no-print {
              display: none !important;
            }
          }
          
          * {
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Roboto Mono', 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.4;
            width: ${this.printerConfig.paperWidth}mm;
            margin: 0 auto;
            padding: 10px;
            background: white;
            color: #000;
          }
          
          .receipt {
            width: 100%;
          }
          
          .header {
            text-align: center;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 2px dashed #000;
          }
          
          .logo {
            margin-bottom: 10px;
          }
          
          .company-name {
            font-size: 20px;
            font-weight: 600;
            margin-bottom: 5px;
            letter-spacing: 1px;
          }
          
          .company-info {
            font-size: 10px;
            color: #333;
            line-height: 1.3;
          }
          
          .order-info {
            margin: 12px 0;
            padding-bottom: 10px;
            border-bottom: 1px dashed #000;
          }
          
          .info-row {
            display: flex;
            justify-content: space-between;
            margin: 4px 0;
            font-size: 11px;
          }
          
          .info-label {
            font-weight: 600;
          }
          
          .items-header {
            font-weight: 600;
            padding: 8px 0;
            border-bottom: 1px solid #000;
            margin-bottom: 8px;
          }
          
          .item-row {
            border-bottom: 1px dotted #ccc;
            padding: 6px 0;
            page-break-inside: avoid;
          }
          
          .item-row:last-child {
            border-bottom: none;
          }
          
          .item-name {
            font-weight: 600;
            font-size: 11px;
            margin-bottom: 2px;
          }
          
          .item-variant {
            font-size: 10px;
            color: #666;
            font-style: italic;
            margin-left: 10px;
          }
          
          .item-details {
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            color: #333;
          }
          
          .item-note {
            font-size: 9px;
            color: #666;
            margin-left: 10px;
            margin-top: 2px;
          }
          
          .totals {
            margin-top: 12px;
            padding-top: 10px;
            border-top: 1px dashed #000;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            margin: 6px 0;
            font-size: 11px;
          }
          
          .discount-row {
            color: #008000;
          }
          
          .grand-total {
            font-size: 14px;
            font-weight: 600;
            border-top: 2px solid #000;
            border-bottom: 2px solid #000;
            padding: 8px 0;
            margin: 10px 0;
          }
          
          .payment-info {
            margin: 12px 0;
            padding: 8px 0;
            border-bottom: 1px dashed #000;
          }
          
          .barcode {
            text-align: center;
            margin: 15px 0;
            font-family: 'Libre Barcode 128', monospace;
            font-size: 48px;
            height: 50px;
            letter-spacing: 2px;
          }
          
          .qr-code {
            text-align: center;
            margin: 15px 0;
          }
          
          .footer {
            text-align: center;
            margin-top: 20px;
            padding-top: 10px;
            font-size: 10px;
            color: #333;
            border-top: 1px dashed #000;
          }
          
          .footer-message {
            font-weight: 600;
            margin-bottom: 5px;
          }
          
          .no-print {
            margin: 20px 0;
            text-align: center;
          }
          
          .print-button {
            background: #6e5199;
            color: white;
            border: none;
            padding: 12px 30px;
            font-size: 14px;
            border-radius: 6px;
            cursor: pointer;
            margin: 0 5px;
          }
          
          .print-button:hover {
            background: #5a4080;
          }
          
          .cancel-button {
            background: #6c757d;
            color: white;
            border: none;
            padding: 12px 30px;
            font-size: 14px;
            border-radius: 6px;
            cursor: pointer;
            margin: 0 5px;
          }
        </style>
        <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+128&display=swap" rel="stylesheet">
      </head>
      <body>
        <div class="receipt">
          <!-- Header -->
          <div class="header">
            ${
              this.printerConfig.printLogo
                ? `
              <div class="logo">
                <!-- Add your logo here -->
                <svg width="60" height="60" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="50" cy="50" r="45" fill="#6e5199"/>
                  <text x="50" y="65" font-family="Arial" font-size="30" fill="white" text-anchor="middle">P360</text>
                </svg>
              </div>
            `
                : ''
            }
            <div class="company-name">${this.restaurantInfo.name}</div>
            <div class="company-info">
              ${this.restaurantInfo.address}<br>
              ${this.restaurantInfo.city}<br>
              Tel: ${this.restaurantInfo.phone}<br>
              ${this.restaurantInfo.taxId}<br>
              ${this.restaurantInfo.email}
            </div>
          </div>
          
          <!-- Order Info -->
          <div class="order-info">
            <div class="info-row">
              <span class="info-label">Order #:</span>
              <span>ORD-${orderId}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Invoice #:</span>
              <span>${invoiceNumber}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Date:</span>
              <span>${moment(orderDate).format('DD/MM/YYYY')}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Time:</span>
              <span>${moment(orderDate).format('HH:mm:ss')}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Type:</span>
              <span>${orderType}</span>
            </div>
            ${
              tableNumber
                ? `
              <div class="info-row">
                <span class="info-label">Table:</span>
                <span>${tableNumber}</span>
              </div>
            `
                : ''
            }
            <div class="info-row">
              <span class="info-label">Customer:</span>
              <span>${customerName || 'Walk-in Customer'}</span>
            </div>
          </div>
          
          <!-- Items -->
          <div class="items-section">
            <div class="items-header">ITEMS</div>
            ${orderItems
              .map(
                (item, index) => `
              <div class="item-row">
                <div class="item-name">${index + 1}. ${item.itemName}</div>
                ${item.selectedVariant ? `<div class="item-variant">${item.selectedVariant}</div>` : ''}
                <div class="item-details">
                  <span>${item.quantity} x ${currencyCode} ${item.menuItemPrice.toFixed(2)}</span>
                  <span>${currencyCode} ${(item.quantity * item.menuItemPrice).toFixed(2)}</span>
                </div>
                ${item.specialInstructions ? `<div class="item-note">📝 ${item.specialInstructions}</div>` : ''}
              </div>
            `
              )
              .join('')}
          </div>
          
          <!-- Totals -->
          <div class="totals">
            <div class="total-row">
              <span>Subtotal:</span>
              <span>${currencyCode} ${subtotal.toFixed(2)}</span>
            </div>
            ${
              tax > 0
                ? `
              <div class="total-row">
                <span>VAT (5%):</span>
                <span>${currencyCode} ${tax.toFixed(2)}</span>
              </div>
            `
                : ''
            }
            ${
              discount > 0
                ? `
              <div class="total-row discount-row">
                <span>Discount:</span>
                <span>-${currencyCode} ${discount.toFixed(2)}</span>
              </div>
            `
                : ''
            }
            <div class="grand-total total-row">
              <span>TOTAL:</span>
              <span>${currencyCode} ${total.toFixed(2)}</span>
            </div>
          </div>
          
          <!-- Payment Info -->
          <div class="payment-info">
            <div class="total-row">
              <span class="info-label">Payment Method:</span>
              <span>${paymentMethod}</span>
            </div>
            <div class="total-row">
              <span class="info-label">Amount Paid:</span>
              <span>${currencyCode} ${amountPaid.toFixed(2)}</span>
            </div>
            ${
              change > 0
                ? `
              <div class="total-row">
                <span class="info-label">Change:</span>
                <span>${currencyCode} ${change.toFixed(2)}</span>
              </div>
            `
                : ''
            }
          </div>
          
          <!-- Barcode -->
          ${
            this.printerConfig.printBarcode
              ? `
            <div class="barcode">
              *${orderId}*
            </div>
          `
              : ''
          }
          
          <!-- QR Code placeholder -->
          ${
            this.printerConfig.printQRCode
              ? `
            <div class="qr-code">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
                qrData
              )}" alt="QR Code" style="width: 120px; height: 120px;">
            </div>
          `
              : ''
          }
          
          <!-- Footer -->
          <div class="footer">
            <div class="footer-message">✨ Thank you for your business! ✨</div>
            <div>We appreciate your visit</div>
            <div>Please come again</div>
            <div style="margin-top: 10px; font-size: 9px;">
              ${this.restaurantInfo.website}<br>
              Powered by Pro360 ERP System
            </div>
          </div>
        </div>
        
        <!-- Print buttons (only visible on screen) -->
        <div class="no-print">
          <button onclick="window.print()" class="print-button">🖨️ Print Receipt</button>
          <button onclick="window.close()" class="cancel-button">Close</button>
        </div>
      </body>
      </html>
    `;

    return html;
  }

  // Print directly without preview
  async printDirectly(html) {
    const printWindow = window.open('', '_blank', 'width=400,height=700,scrollbars=yes');

    if (!printWindow) {
      throw new Error('Pop-up blocked. Please allow pop-ups for printing.');
    }

    printWindow.document.write(html);
    printWindow.document.close();

    // Wait for content to load
    return new Promise((resolve) => {
      printWindow.onload = () => {
        printWindow.focus();

        // Auto-print
        setTimeout(() => {
          printWindow.print();

          // Auto-close after print dialog
          setTimeout(() => {
            printWindow.close();
            resolve({ success: true, method: 'browser' });
          }, 1000);
        }, 500);
      };
    });
  }

  // Print with preview
  async printWithPreview(html) {
    const printWindow = window.open('', '_blank', 'width=450,height=750,scrollbars=yes');

    if (!printWindow) {
      throw new Error('Pop-up blocked. Please allow pop-ups for printing.');
    }

    printWindow.document.write(html);
    printWindow.document.close();

    return { success: true, method: 'browser-preview' };
  }

  // Silent print (for kiosk mode - requires browser configuration)
  async silentPrint(html) {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    iframe.contentDocument.write(html);
    iframe.contentDocument.close();

    iframe.contentWindow.focus();
    iframe.contentWindow.print();

    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);

    return { success: true, method: 'silent' };
  }

  // Test printer with sample data
  async testPrinter() {
    const testData = {
      orderId: 'TEST-' + Date.now().toString().slice(-6),
      orderDate: new Date(),
      orderItems: [
        {
          itemName: 'Chicken Shawarma',
          quantity: 2,
          menuItemPrice: 25.0,
          selectedVariant: 'Large',
          specialInstructions: 'Extra garlic sauce',
        },
        {
          itemName: 'Fresh Orange Juice',
          quantity: 1,
          menuItemPrice: 18.0,
          selectedVariant: null,
          specialInstructions: '',
        },
        {
          itemName: 'Hummus Plate',
          quantity: 1,
          menuItemPrice: 22.0,
          selectedVariant: null,
          specialInstructions: 'No onions',
        },
      ],
      subtotal: 90.0,
      tax: 4.5,
      discount: 5.0,
      total: 89.5,
      amountPaid: 100.0,
      change: 10.5,
      customerName: 'Test Customer',
      orderType: 'Dine-In',
      tableNumber: 'T-05',
      invoiceNumber: 'INV-TEST-' + Date.now(),
      paymentMethod: 'Cash',
    };

    return this.printReceipt(testData, { code: 'AED' });
  }

  // Save receipt as PDF (using browser's print to PDF)
  async saveAsPDF(orderData, currency) {
    const html = this.generateReceiptHTML(orderData, currency);
    const printWindow = window.open('', '_blank');

    printWindow.document.write(html);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.print();
    };

    return { success: true, method: 'pdf' };
  }
}

export default new ReceiptPrintService();
